import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useCrmMyAccess } from '@/hooks/queries';
import * as Sentry from '@sentry/browser';

export default function Navigation() {
  const { user, signOut, authFetch } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isOpeningBilling, setIsOpeningBilling] = useState(false);
  
  // Check CRM access (admins or salespeople)
  const { data: crmAccess } = useCrmMyAccess();

  const handleSignOut = async () => {
    if (isSigningOut) return;
    
    try {
      setIsSigningOut(true);
      await signOut();
      // Redirect to landing page after successful sign out
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Sign out failed:', error);
      Sentry.captureException(error, {
        extra: { action: 'handleSignOut', location: 'Navigation' }
      });
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleOpenBilling = async () => {
    if (isOpeningBilling) return;
    
    try {
      setIsOpeningBilling(true);
      const returnUrl = `${window.location.origin}/dashboard`;
      const response = await authFetch(`/api/users/customer-portal?returnUrl=${encodeURIComponent(returnUrl)}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to open billing portal');
      }
      
      const data = await response.json();
      window.location.href = data.portalUrl;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      Sentry.captureException(error, {
        extra: { action: 'handleOpenBilling', location: 'Navigation' }
      });
    } finally {
      setIsOpeningBilling(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center">
              <img 
                src="https://zapt-cdn.com/dc408fb4-e206-4be6-8677-dfc0f17d99f7/Zapt_Navy%2BLogo%2B(1).png" 
                alt="ZAPT" 
                className="h-10 w-auto"
              />
            </Link>
          </div>

          <div className="hidden sm:flex sm:items-center sm:space-x-8">
            {(user?.email === 'david@zapt.ai' || user?.email === 'phil@zapt.ai') && (
              <Link
                to="/admin-portal"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  location.pathname.startsWith('/admin-portal') 
                    ? 'text-zapt-purple bg-zapt-purple/10' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Admin Portal
              </Link>
            )}
            
            {crmAccess?.canAccessCrm && (
              <Link
                to="/crm"
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  location.pathname.startsWith('/crm') 
                    ? 'text-zapt-purple bg-zapt-purple/10' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                CRM
              </Link>
            )}
            
            {/* Account Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-1 ${
                  location.pathname === '/account'
                    ? 'text-zapt-purple bg-zapt-purple/10' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Account
                <svg className={`w-4 h-4 transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isAccountMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setIsAccountMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                    <div className="py-1" role="menu">
                      <Link
                        to="/account"
                        onClick={() => setIsAccountMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Account Settings
                      </Link>
                      <button
                        onClick={() => {
                          setIsAccountMenuOpen(false);
                          handleOpenBilling();
                        }}
                        disabled={isOpeningBilling}
                        className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        {isOpeningBilling ? 'Opening...' : 'Billing & Invoices'}
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={() => {
                          setIsAccountMenuOpen(false);
                          handleSignOut();
                        }}
                        disabled={isSigningOut}
                        className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      >
                        {isSigningOut ? 'Signing out...' : 'Sign out'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="sm:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 cursor-pointer"
            >
              <svg
                className="h-6 w-6"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="sm:hidden">
            <div className="pt-2 pb-3 space-y-1">
              {(user?.email === 'david@zapt.ai' || user?.email === 'phil@zapt.ai') && (
                <Link
                  to="/admin-portal"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    location.pathname.startsWith('/admin-portal')
                      ? 'text-zapt-purple bg-zapt-purple/10'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Admin Portal
                </Link>
              )}
              
              {crmAccess?.canAccessCrm && (
                <Link
                  to="/crm"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    location.pathname.startsWith('/crm')
                      ? 'text-zapt-purple bg-zapt-purple/10'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  CRM
                </Link>
              )}
              
              <Link
                to="/account"
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive('/account')
                    ? 'text-zapt-purple bg-zapt-purple/10'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Account Settings
              </Link>
              
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  handleOpenBilling();
                }}
                disabled={isOpeningBilling}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-50"
              >
                {isOpeningBilling ? 'Opening...' : 'Billing & Invoices'}
              </button>
            </div>
            
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="px-3">
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleSignOut();
                  }}
                  disabled={isSigningOut}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 cursor-pointer"
                >
                  {isSigningOut ? 'Signing out...' : 'Sign out'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}